import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, Mail, Lock, Send, Loader2, Bike, Store, Globe } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const Signup = () => {
  const { t } = useTranslation();
  const { signup, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const intent = params.get('intent');
  const next = params.get('next');
  const prefillUrl = params.get('prefill_url') || '';
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [vendorUrl, setVendorUrl] = useState(prefillUrl);
  const [busy, setBusy] = useState(false);

  const intentMeta = intent === 'driver'
    ? { icon: Bike, label: 'driver portal' }
    : intent === 'vendor' ? { icon: Store, label: 'vendor onboarding' } : null;

  const submit = async (e) => {
    e.preventDefault();
    // If the vendor pasted a URL, route them through onboarding with auto-scan.
    // The <Public> route guard redirects to ?next= the moment the user is set
    // in AuthContext. So we pre-update the URL via React Router *before* awaiting
    // signup — that way Public reads the right next when it re-renders.
    if (intent === 'vendor' && vendorUrl.trim()) {
      const u = vendorUrl.trim();
      const target = `/vendor-onboarding?prefill_url=${encodeURIComponent(u)}&autoscan=1`;
      nav(`/signup?intent=vendor&next=${encodeURIComponent(target)}`, { replace: true });
    }
    setBusy(true);
    try {
      await signup(form);
      toast.success(intent ? `Account created! Taking you to ${intentMeta.label}…` : 'Account created. Welcome to ShipitNow!');
      // <Public> wrapper auto-redirects to ?next= once user is set — nothing to do here.
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Signup failed';
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl shadow-slate-200/60 p-8 sm:p-10 fade-in">
        <div className="flex justify-center -mt-12 mb-4">
          <img src="/shipitnow-logo.png" alt="ShipitNow" className="h-14 w-auto" />
        </div>
        <h1 className="text-3xl font-semibold text-slate-900 text-center tracking-tight">{t('auth.create_account', 'Create your account')}</h1>
        <p className="text-center text-slate-500 mt-1">{t('auth.get_started', 'Get started with ShipitNow')}</p>

        {intentMeta && (
          <div className="mt-5 rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex items-center gap-2 text-sm" data-testid="intent-banner">
            <intentMeta.icon className="h-4 w-4 text-emerald-700" />
            <span className="text-emerald-800">Customer account first → then onto <span className="font-semibold">{intentMeta.label}</span>.</span>
          </div>
        )}

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="name">{t('auth.full_name', 'Full name')}</Label>
            <div className="relative mt-1.5">
              <User className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input id="name" placeholder="Jane Doe" className="pl-9 h-11" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
          </div>
          <div>
            <Label htmlFor="email">{t('auth.email', 'Email')}</Label>
            <div className="relative mt-1.5">
              <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input id="email" type="email" placeholder="you@example.com" className="pl-9 h-11" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </div>
          </div>
          <div>
            <Label htmlFor="password">{t('auth.password', 'Password')}</Label>
            <div className="relative mt-1.5">
              <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input id="password" type="password" placeholder="At least 6 characters" className="pl-9 h-11" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} />
            </div>
          </div>
          {intent === 'vendor' && (
            <div data-testid="signup-vendor-url-wrapper">
              <Label htmlFor="vendor-url" className="flex items-center gap-1.5">
                Your website <span className="text-[11px] text-slate-400 font-normal">(optional — we'll auto-build your store)</span>
              </Label>
              <div className="relative mt-1.5">
                <Globe className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="vendor-url"
                  type="url"
                  placeholder="https://yourstore.com"
                  className="pl-9 h-11"
                  value={vendorUrl}
                  onChange={(e) => setVendorUrl(e.target.value)}
                  data-testid="signup-vendor-url-input"
                />
              </div>
              <p className="text-[11px] text-emerald-700 mt-1.5">Skip if you don't have one — you can pick a template after signup.</p>
            </div>
          )}
          <Button type="submit" disabled={busy} className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {t('nav.sign_up', 'Create account')}
          </Button>
        </form>

        <button onClick={loginWithGoogle} className="mt-3 w-full h-11 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors text-slate-700 font-medium">
          {t('auth.continue_with_google', 'Continue with Google')}
        </button>

        <p className="mt-5 text-center text-sm text-slate-500">{t('auth.have_account', 'Already have an account?')} <Link to={`/login${intent ? `?intent=${intent}&next=${encodeURIComponent(next || '')}` : ''}`} className="text-slate-900 font-medium hover:underline">{t('nav.sign_in', 'Sign in')}</Link></p>

        {!intentMeta && (
          <div className="mt-6 pt-5 border-t border-slate-100">
            <p className="text-[11px] text-slate-400 uppercase tracking-widest font-semibold text-center">
              {t('auth.or_join_as', 'Or join as')}
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Link
                to="/signup?intent=vendor&next=%2Fvendor-onboarding"
                data-testid="signup-cta-vendor"
                className="flex items-center justify-center gap-1.5 h-10 rounded-lg border border-emerald-200 bg-emerald-50/50 hover:bg-emerald-50 text-emerald-800 text-sm font-medium transition-colors"
              >
                <Store className="h-4 w-4" />
                {t('home.become_vendor', 'Become a vendor')}
              </Link>
              <Link
                to="/signup?intent=driver&next=%2Fdriver%2Fapply"
                data-testid="signup-cta-driver"
                className="flex items-center justify-center gap-1.5 h-10 rounded-lg border border-slate-200 bg-slate-50/60 hover:bg-slate-100 text-slate-800 text-sm font-medium transition-colors"
              >
                <Bike className="h-4 w-4" />
                {t('home.become_driver', 'Become a driver')}
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Signup;
